/** Version 1 is mirrored in the native app; backend outcomes have one API emitter. */
export const ANALYTICS_SCHEMA_VERSION = 1;
export const screens = [
  "trips",
  "trip",
  "trip_create",
  "trip_summary",
  "trip_details",
  "trip_events",
  "trip_costs",
  "trip_attachments",
  "trip_travellers",
  "packing_lists",
  "packing_list",
  "packing_list_create",
  "countries",
  "country",
  "bucket_list",
  "statistics",
  "translate",
  "settings",
  "subscription",
  "plans",
  "profile",
  "onboarding",
] as const;
export type AnalyticsScreen = (typeof screens)[number];
export const features = [
  "trip_create",
  "packing_list_create",
  "import",
  "subscription",
] as const;
export type AnalyticsFeature = (typeof features)[number];
export type AnalyticsPlatform = "web" | "ios" | "android";
export type AnalyticsEvent = {
  eventId: string;
  name: "screen_view" | "feature_opened";
  properties: {
    schema_version: 1;
    platform: AnalyticsPlatform;
    screen?: AnalyticsScreen;
    feature?: AnalyticsFeature;
  };
};
export type AnalyticsContext = {
  // The auth ID is used only for local account isolation; never put it in payloads.
  actorKey: string | null;
  consented: boolean;
  enabled: boolean;
};
export type AnalyticsTransport = (
  event: AnalyticsEvent,
  signal: AbortSignal,
  actorKey: string,
) => Promise<void>;
/** No SDK queue, retry, persistent identity, automatic capture or raw URL input. */
export function createAnalytics(
  platform: AnalyticsPlatform,
  transport: AnalyticsTransport,
  eventId: () => string,
) {
  let context: AnalyticsContext = {
    actorKey: null,
    consented: false,
    enabled: false,
  };
  let previousScreen: AnalyticsScreen | null = null;
  let previousVisit: string | null = null;
  const pending = new Set<AbortController>();
  const reset = () => {
    for (const request of pending) request.abort();
    pending.clear();
    previousScreen = null;
    previousVisit = null;
  };
  const allowed = () =>
    Boolean(context.enabled && context.consented && context.actorKey);
  const send = (
    name: AnalyticsEvent["name"],
    dimension: { screen: AnalyticsScreen } | { feature: AnalyticsFeature },
  ) => {
    if (!allowed() || !context.actorKey) return;
    if (pending.size >= 10) return;
    let id: string;
    try {
      id = eventId();
    } catch {
      return;
    }
    const actorKey = context.actorKey;
    const request = new AbortController();
    pending.add(request);
    const timeout = setTimeout(() => request.abort(), 5000);
    const event: AnalyticsEvent = {
      eventId: id,
      name,
      properties: {
        schema_version: ANALYTICS_SCHEMA_VERSION,
        platform,
        ...dimension,
      },
    };
    // Defer one microtask and recheck abort, so same-turn logout cannot dispatch.
    void Promise.resolve()
      .then(() =>
        request.signal.aborted
          ? undefined
          : transport(event, request.signal, actorKey),
      )
      .catch(() => {
        /* Analytics failure never blocks product use and is not retried. */
      })
      .finally(() => {
        clearTimeout(timeout);
        pending.delete(request);
      });
  };
  return {
    setContext(next: AnalyticsContext) {
      if (
        Object.keys(context).some(
          (key) =>
            context[key as keyof AnalyticsContext] !==
            next[key as keyof AnalyticsContext],
        )
      )
        reset();
      context = {
        actorKey: next.actorKey,
        consented: next.consented === true,
        enabled: next.enabled === true,
      };
    },
    screen(
      screen: AnalyticsScreen | null,
      localVisitKey: string = screen ?? "",
    ) {
      if (!screen || !screens.includes(screen) || !allowed()) {
        previousScreen = null;
        previousVisit = null;
        return;
      }
      if (screen === previousScreen && localVisitKey === previousVisit) return;
      previousScreen = screen;
      previousVisit = localVisitKey;
      send("screen_view", { screen });
    },
    feature(feature: AnalyticsFeature) {
      if (features.includes(feature)) send("feature_opened", { feature });
    },
    clear() {
      reset();
      context = { actorKey: null, consented: false, enabled: false };
    },
  };
}
